// Generated by Copilot- Start
import { useCallback, useMemo, useState } from "react";
import { ChannelCardV2 } from "lib/helper/channel-interactive-tool-v2";

export interface SearchState {
  selectedChannels: ChannelCardV2[];
  selectedSvods: any[];
  selectedShows: any[];
}

export interface SearchHandlers {
  selectedChannels: ChannelCardV2[];
  selectedSvods: any[];
  selectedShows: any[];
  updateSearch: (selectedData: any[]) => void;
  handleShowSearch: () => boolean;
  searchedChannelsByCategory: Record<string, ChannelCardV2[]>;
  searchedCategories: Array<{ key: string; value: string }>;
  searchedAddonChannelsByCategory: Record<string, ChannelCardV2[]>; // NEW: Addon channels separated
  searchedAddonCategories: Array<{ key: string; value: string }>; // NEW: Categories with addons
}

export const useSearchHandlers = (
  allCategories: Array<{ key: string; value: string }> | undefined,
  showGenrePackages: boolean | undefined,
  showMyFreeDTVChannels: boolean | undefined,
  enableSearch: boolean | undefined,
  allAddOnChannels?: string[] // NEW: List of addon channel names
): SearchHandlers => {
  const [selectedChannels, setSelectedChannels] = useState<ChannelCardV2[]>([]);
  const [selectedSvods, setSelectedSvods] = useState<any[]>([]);
  const [selectedShows, setSelectedShows] = useState<any[]>([]);

  const updateSearch = useCallback((selectedData: any[]) => {
    if (!selectedData || selectedData.length === 0) {
      setSelectedChannels([]);
      setSelectedSvods([]);
      setSelectedShows([]);
      return;
    }

    const tempSvodData: any[] = [];
    const resourceIdData: any[] = [];
    const otherData: any[] = [];

    selectedData.forEach(item => {
      if (item?.resourceId) {
        resourceIdData.push(item);
      } else if (item?.svodData) {
        tempSvodData.push(item);
      } else {
        otherData.push(item);
      }
    });

    setSelectedShows(resourceIdData);
    setSelectedSvods(tempSvodData);
    setSelectedChannels(otherData);
  }, []);

  const handleShowSearch = useCallback(() => {
    if (showGenrePackages || showMyFreeDTVChannels) {
      return false;
    }
    return enableSearch !== false;
  }, [showGenrePackages, showMyFreeDTVChannels, enableSearch]);

  const searchedChannelsByCategory = useMemo(() => {
    if (selectedChannels.length === 0) {
      return {};
    }

    // Separate regular channels from addon channels
    const regularChannels = selectedChannels.filter(channel => {
      // Check if channel is in allAddOnChannels list
      const isAddon = allAddOnChannels?.includes(channel.label);
      return !isAddon;
    });

    return regularChannels.reduce((acc, channel) => {
      const categoryId = channel.categoryId || "OTHER";
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(channel);
      return acc;
    }, {} as Record<string, ChannelCardV2[]>);
  }, [selectedChannels, allAddOnChannels]);

  // NEW: Separate addon channels by category
  const searchedAddonChannelsByCategory = useMemo(() => {
    if (selectedChannels.length === 0) {
      return {};
    }

    // Filter only addon channels
    const addonChannels = selectedChannels.filter(channel => {
      const isAddon = allAddOnChannels?.includes(channel.label);
      return isAddon;
    });

    return addonChannels.reduce((acc, channel) => {
      const categoryId = channel.categoryId || "OTHER";
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(channel);
      return acc;
    }, {} as Record<string, ChannelCardV2[]>);
  }, [selectedChannels, allAddOnChannels]);

  const searchedCategories = useMemo(() => {
    if (!allCategories || selectedChannels.length === 0) {
      return [];
    }

    const categoryIds = Object.keys(searchedChannelsByCategory);
    return allCategories.filter(cat => categoryIds.includes(cat.key));
  }, [allCategories, searchedChannelsByCategory, selectedChannels.length]);

  // NEW: Categories that have addon channels
  const searchedAddonCategories = useMemo(() => {
    if (!allCategories || selectedChannels.length === 0) {
      return [];
    }

    const categoryIds = Object.keys(searchedAddonChannelsByCategory);
    return allCategories.filter(cat => categoryIds.includes(cat.key));
  }, [allCategories, searchedAddonChannelsByCategory, selectedChannels.length]);

  return {
    selectedChannels,
    selectedSvods,
    selectedShows,
    updateSearch,
    handleShowSearch,
    searchedChannelsByCategory,
    searchedCategories,
    searchedAddonChannelsByCategory,
    searchedAddonCategories
  };
};
// Generated by Copilot- End
